import {
  EnumField,
  LoggerFactory,
  Required,
  SwitchField,
  Type,
  valid,
  Validator
} from "lego-gui-lib";
import { t } from "../core/i18n/i18n";
import { ReactText, useEffect, useState } from "react";
import { FormState } from "final-form";
import { mainStore } from "../core/mainStore";
import { RemoteDataSource } from "lego-gui-lib/lib";

const log = LoggerFactory.create("common");

export const alwaysTrueFn = (): true => true;

export type LabelFieldOption = {
  [k: string]: ReactText;
};

export enum VisibilityType {
  PUBLIC = 0,
  PRIVATE = 1
}

export const TIME_RANGE_PATTERN =
  "([01]?[0-9]|2[0-3]):[0-5][0-9]\\-([01]?[0-9]|2[0-3]):[0-5][0-9]";
export const ADVANCED_CONDITION_SIMPLE_OUTPUT_PATTERN_XRN =
  "^(D[\\dABCDE]{1,4})$";
export const ADVANCED_CONDITION_SIMPLE_OUTPUT_PATTERN_LAST_SIP_RESPONSE_CODE =
  "^(\\d{3})$";
export const ADVANCED_CONDITION_SIMPLE_OUTPUT_PATTERN_SAN_EXTENSION =
  "^(\\d{1,14})$";
export const ADVANCED_CONDITION_SIMPLE_OUTPUT_PATTERN_NUMBER = "^[1-9]{1}[0-9]{0,19}$";
export const NUMERIC_RANGE_PATTERN = "\\d{1,9}\\-\\d{1,9}";

export function VisibilityEnum(): Type {
  return EnumField(
    [
      {
        label: t("hns:messageData:visibility.public"),
        value: VisibilityType.PUBLIC
      },
      {
        label: t("hns:messageData:visibility.private"),
        value: VisibilityType.PRIVATE
      }
    ],
    Required
  );
}

export const BooleanFieldWithBooleans = (...validators: Validator[]) =>
  EnumField(
    [
      { label: t("common:booleanEnum:yes"), value: true },
      { label: t("common:booleanEnum:no"), value: false }
    ],
    ...validators
  );

export const BooleanSwitchField = (...validators: Validator[]) =>
  SwitchField(
    {
      on: { label: t("common:booleanEnum:yes"), value: 1 },
      off: { label: t("common:booleanEnum:no"), value: 0 }
    },
    ...validators
  );

export const BooleanField = (...validators: Validator[]): Type => {
  const BooleanOptions = [
    { label: t("common:booleanEnum:yes", "YES"), value: 1 },
    { label: t("common:booleanEnum:no", "NO"), value: 0 }
  ];

  return EnumField(BooleanOptions, ...validators);
};

export const useFirstValue = <T extends {}>(
  dataSource: T,
  accessor: (ds: T) => any
) => {
  const [value, setValue] = useState(undefined as any);

  useEffect(() => {
    if (!value && dataSource) {
      setValue(accessor(dataSource));
    }
  }, [accessor, dataSource, value]);

  return value;
};

export const withExternalId = (fieldName?: string) => {
  fieldName = fieldName || "name";

  return (option: {}) => {
    // @ts-ignore
    const externalId = option.externalId;
    // @ts-ignore
    const name = option[fieldName];

    return externalId ? `${name} <b>[${externalId}]</b>` : `${name}`;
  };
};

export const withVisibility = (fieldName?: string) => {
  fieldName = fieldName || "name";

  return (option: {}) => {
    // @ts-ignore
    const name = option[fieldName];
    // @ts-ignore
    const visibility = option.visibility;

    return visibility === VisibilityType.PRIVATE
      ? `${name} [${t("common:private")()}]`
      : `${name} [${t("common:public")()}]`;
  };
};

/** Visibility PUBLIC forbids customerRef in a dynamicForm, but customerRef field is visible only to Admin
 * Cleanup - namely customer.oneDataSource.clear() is disabled for CustomerOwner so the customer context switching works as expected */
export const onVisibilityChange = (
  newValue: VisibilityType,
  oldValue: VisibilityType,
  formState: FormState<any>
): void => {
  if (newValue === VisibilityType.PUBLIC && mainStore.auth.isAdmin()) {
    formState.values["customerRef"] = null;
    mainStore.customer.oneDataSource.clear();
  }
};

export const onVisibilityInit = (newValue: VisibilityType): void => {
  if (newValue === VisibilityType.PUBLIC && mainStore.auth.isAdmin()) {
    mainStore.customer.oneDataSource.clear();
  }
};

/** Customer context helpers */
export const customerChanged = (
  newValue: string,
  oldValue: string,
  formState: FormState<any>
): void => {
  log.debug("customerChanged", newValue, oldValue, formState);
  if (newValue !== oldValue) {
    mainStore.customer.oneDataSource.findOne({ id: newValue });
    formState.values["sanRef"] = undefined;
  } else {
    log.debug(
      "not reloading customer because it is the same as before",
      newValue,
      oldValue
    );
  }
};

export const customerInit = (
  newValue: string,
  oldValue: string,
  formState: FormState<any>
): void => {
  log.debug("customerInit", newValue, oldValue, formState);
  mainStore.customer.oneDataSource.findOne({ id: newValue });
};

export var ReportDefinitionValidator = valid(
  function(v: any, values: any, meta: any) {
    if (v === true) {
      return true;
    } else {
      //check for valid fields
      for (const key in values.definition) {
        if (values.definition[key] === true) {
          return true;
        }
      }
      return false;
    }
  },
  "lego:validators.reportValidator",
  "At least one type must be included"
);

export const DigitsEnum = [
  { label: "0", value: "0" },
  { label: "1", value: "1" },
  { label: "2", value: "2" },
  { label: "3", value: "3" },
  { label: "4", value: "4" },
  { label: "5", value: "5" },
  { label: "6", value: "6" },
  { label: "7", value: "7" },
  { label: "8", value: "8" },
  { label: "9", value: "9" },
  { label: "*", value: "B" },
  { label: "#", value: "C" }
];

export const EmailOrEmpty: Validator = valid(
  (text: string) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return text ? re.test(String(text).toLowerCase()) : true;
  },
  "lego:validators.email",
  "Must be an email"
);

export const getBaseUrl = function() {
  return `${window.location.protocol}//${window.location.host}${process.env
    .PUBLIC_URL || ""}`;
};

export function getCookie(name: string): string | null {
  const nameLenPlus = name.length + 1;
  return (
    document.cookie
      .split(";")
      .map(c => c.trim())
      .filter(cookie => {
        return cookie.substring(0, nameLenPlus) === `${name}=`;
      })
      .map(cookie => {
        return decodeURIComponent(cookie.substring(nameLenPlus));
      })[0] || null
  );
}

/**
 * fn is used to check which row buttons are allowed
 * can edit if admin or visibility is private and user is not a customer_read_only
 * if DS has not visibility, condition ==> "ds.oneDataSource.one?.visibility !== VisibilityType.PUBLIC" will return true
 * but we still demand customer with rights to write or admin
 */
export const isAllowed = function(ds: RemoteDataSource<any, any>) {
  return (
    mainStore.auth.isAdmin() ||
    (ds.oneDataSource.one?.visibility !== VisibilityType.PUBLIC &&
      !mainStore.auth.isReadOnly())
  );
};
